import React, { useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, List, TextField } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { restoreTerm } from './languageTermsApi'
import { matchesSearch } from './termFilters'
import ConfirmChangeDialog from './ConfirmChangeDialog'
import HistoryEntry from './HistoryEntry'
import { useTermRequestFlow } from './useTermRequestFlow'
import { NO_SYSTEM_VALUES } from './systemValueTerms'

/**
 * Historia 176 — CU7/CU8, T7 (S120, minimal: presence + restore). Every entry of
 * `GET …/terms/history` (2.4), unfiltered by key (RN24b): reaches a key whose row is no longer
 * shown at all — e.g. a detached key just undone, gone from every filter (RN22) — so restoring it
 * cannot depend on the row still being on screen.
 */
// 03-dominio.md §6 "Clase de una clave": same marks as the main table (S127).
const CLASS_MARK_KEY = { outsideCatalogue: 'Outside the catalogue', detached: 'Detached', systemValue: 'System value' }

const LanguageHistoryDialog = ({
  open = false,
  code = '',
  entries = [],
  loadErrorKey = '',
  terms = {},
  inheritedOf = () => '',
  readOnly = false,
  artifact = {},
  systemValues = NO_SYSTEM_VALUES,
  classOf = () => 'label',
  onClose = () => {},
  onFileUnavailable = () => {},
  onRestored = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  // RN24b, reusing T9: local filter over entries already fetched, no new request to Sun (S128).
  const [searchQuery, setSearchQuery] = useState('')
  // RN9/S121/S122/S153: the very same request flow a row uses (B1: one function, not a copy).
  const flow = useTermRequestFlow({ code, artifact, systemValues, onFileUnavailable, onApplied: (termKey, result) => onRestored(termKey, result.value, result.change) })

  // RN9b: `from` = what is painted today for the key (its term, or the inherited one), `to` = the state (new value) of the entry.
  // RN16d: what is sent is F's id (`changeId`), the next more recent entry of the SAME key, found by `HistoryEntry`.
  const handleRestore = (entry, changeId) => flow.submit(entry.key, () => restoreTerm(code, changeId), {
    from: terms[entry.key] || inheritedOf(entry.key),
    to: entry.newValue === null ? inheritedOf(entry.key) : entry.newValue
  })

  const visibleEntries = entries.filter((entry) => matchesSearch(searchQuery, [entry.key]))

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t_('Language history')}</DialogTitle>
      <DialogContent>
        {loadErrorKey !== '' ? <Box role='alert'>{t_(loadErrorKey)}</Box> : null}
        {flow.errorKey !== '' ? <Box role='alert'>{flow.errorMessage}</Box> : null}
        <TextField
          size='small'
          label={t_('Search a text')}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
        <List>
          {visibleEntries.map((entry, index) => (
            <HistoryEntry
              key={entry.id}
              isLast={index === visibleEntries.length - 1}
              entry={entry}
              entries={entries}
              currentValue={terms[entry.key] || ''}
              readOnly={readOnly}
              showKey
              classMarkKey={CLASS_MARK_KEY[classOf(entry.key)] || ''}
              onRestore={handleRestore}
            />
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t_('Close')}</Button>
      </DialogActions>

      <ConfirmChangeDialog
        open={flow.confirmOpen}
        termKey={flow.pendingTermKey}
        from={flow.pendingChange.from}
        to={flow.pendingChange.to}
        artifact={artifact}
        systemValues={systemValues}
        onConfirm={flow.handleConfirm}
        onCancel={flow.handleCancel}
      />
    </Dialog>
  )
}

export default LanguageHistoryDialog
